import type {
  FetchUserDataFn,
  GetCurrentUserFn,
  ListWorkspacesFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
  SearchMembersFn,
} from '@/features/iam'
import type { User } from '@/features/iam/users/User'
import type { FetchJobFn, PrimeJobFn } from '@/features/jobs'
import { env } from '@/infra/env'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import type { Observable } from 'rxjs'
import type { Id } from '@/infra/id'
import type { Member } from '@/features/iam/members/Member'
import type { RedirectToStripeConnectFn } from '@/features/billing'
import type { PayoutMethodInfo } from '@/features/payouts/admin/AdminPayoutMethodStore'
import type { ProfilePayoutMethodViewModel } from '@/features/payouts/pro/ProfilePayoutMethodViewModel'

/**
 * Options for {@link PayoutsFeature}.
 */
export interface PayoutsFeatureOptions {
  token$: Observable<string>
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  currentUser$: Observable<User>
  getCurrentUser: GetCurrentUserFn
  primeUser: PrimeUserFn
  primeWorkspace: PrimeWorkspaceFn
  listWorkspaces: ListWorkspacesFn
  searchMembers: SearchMembersFn
  fetchUserData: FetchUserDataFn
  primeJob: PromiseLike<PrimeJobFn>
  fetchJob: FetchJobFn
  redirectToStripeConnect: RedirectToStripeConnectFn
}

/**
 * Fetches the status of a user's payout method.
 */
export type FetchPayoutMethodInfoFn = (
  userPublicId: Id,
  signal?: AbortSignal,
) => Promise<PayoutMethodInfo>

/**
 * Creates the view model for a member's own payout method.
 */
export type ProfilePayoutMethodViewModelFactory = (
  member: Member,
) => ProfilePayoutMethodViewModel

/**
 * Reactively determines whether the current user can see the payouts admin
 * pages.
 */
export type CanViewPayoutsAdminFn = () => boolean

/**
 * Whether payouts are configured for this environment.
 */
export type IsPayoutsEnabledFn = () => boolean

/**
 * Payouts for Pros, and their administration.
 */
export type PayoutsFeature = ReturnType<typeof PayoutsFeature>

export function PayoutsFeature(options: PayoutsFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalPayoutsFeature(options),
  )

  // Without the env var, nothing that calls the payouts APIs is constructed.
  const isEnabled: IsPayoutsEnabledFn = () => Boolean(env.BIGMONEY_API)

  const fetchPayoutMethodInfo: FetchPayoutMethodInfoFn = internal.wrap(
    (m) => (userPublicId: Id, signal?: AbortSignal) =>
      m.adminPayoutMethodStore.fetchPayoutMethodInfo(userPublicId, signal),
  )

  const canViewPayoutsAdmin: CanViewPayoutsAdminFn = () =>
    isEnabled() && options.getCurrentUser().platformAdmin

  return {
    // Internal
    internal,

    // Public
    isEnabled,
    fetchPayoutMethodInfo,
    createProfilePayoutMethodViewModel: internal.lazy(
      (m) => m.createProfilePayoutMethodViewModel,
    ),
    canViewPayoutsAdmin,
    canViewProPayouts$: internal.defer((m) => m.canViewProPayouts$),
  }
}
